import i18n from '../translations/i18n';
import { Accessibility } from "../models/Accessibility";
import axios, { AxiosResponse } from "axios";
import { AccessibilityUnitsQuery } from "../models/AccessibilityUnits";
import React, { KeyboardEvent } from 'react';
import { IContract } from "../models/Contract";
import { RouteType } from "../types/routes";
import {IUser} from "../models/User";
import {A11yStandardType} from "../models/utils";
import {Company} from "../models/Company";
import {Clp} from "../models/Clp";

export const capitalize = (str: string) => {
    if (typeof str !== 'string') return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
}

export const isValidInt = (min: number, max: number) =>
    (inputEvent: any): boolean => {

        const inputValue = parseInt(inputEvent.target.value)

        // eslint-disable-next-line no-mixed-operators
        return !inputValue || inputValue >= min && inputValue <= max
    }

export const trimNumberString = (event: any) => {
    event.target.value = !!event.target.value ? parseInt(event.target.value).toString() : 0;
}

export const preventNonDigitSymbolInput = (keyboardEvent: KeyboardEvent) => {
    if (['.', '+', 'e'].includes(keyboardEvent.key)) {
        keyboardEvent.preventDefault();
    }
}
export function geocodeByLatLng(latLng: {
    lat: number;
    lng: number;
}): Promise<google.maps.GeocoderResult[]> {
    const geocoder = new google.maps.Geocoder();
    const { OK } = google.maps.GeocoderStatus;
    return new Promise((resolve, reject) => {
        geocoder.geocode({
            location: latLng,
        }, (results, status) => {
            if (status !== OK) {
                reject(status);
            } else {
                resolve(results!);
            }
        })
    })
};
export const decodePin = async (val: {
    lat: number;
    lng: number;
}) => {
    const results = await geocodeByLatLng({
        lat: val.lat,
        lng: val.lng
    })
    return results[0]
}

export const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
        element!.scrollIntoView({
            behavior: "smooth",
        });
    }
}

/*
  NOTE! When adding a new language please check first if the language has LCID codes: https://wiki.freepascal.org/Language_Codes
  If it has, include them in the conditional check below like we do for Swedish!
  This is because we have the browser language detector and it will first detect the LCID code.
*/

export const getCurrectLanguageFullName = () => {
    // returns currect language full name like: English/Swedish not en/sv
    let lang;
    if (i18n.language === "en") {
        lang = i18n.t("LanguagesDropdown_English");
    } else if ((i18n.language === "sv") || (i18n.language === "sv-SE")) {
        lang = i18n.t("LanguagesDropdown_Swedish");
    } else if (i18n.language === "da") {
        lang = i18n.t("LanguagesDropdown_Danish");
    }
    return lang;
}

export const getCountsDisabilityTypes = (accessibilityList: Accessibility[]) => {
    const mappedDisabilityTypes = accessibilityList
      .filter((item) => !!item)
      .map((item: any) => item.disability_types)
    const mergedList = [].concat.apply([], mappedDisabilityTypes)
      .filter((item) => !!item);

    enum AccessibilityDataType {
        statsPage_mobility = 'M',
        statsPage_cognitive = 'C',
        statsPage_hearing = 'H',
        statsPage_visual = 'V',
        statsPage_allergy = 'A',
    }

    let countedData: Array<{name: string, value: number}> = []

    const getCount = (s: string, arr: Array<string>): number => {
        return arr.filter((i) => i.indexOf(s) > -1).length;
    }

    for (let item in AccessibilityDataType) {
        countedData.push({
            name: item,
            value: getCount((AccessibilityDataType as any)[item], mergedList)
        })
    }
    return countedData
}

export const removeLeadingZeros = (value: any) => {
    value = String(value).replace(/^0+/, '')
    value = value === '-0' ? '0' : value
    return value ? value : '0'
}

const trackedUrl: Array<{keyword: string, path: string}> = [
    {
        keyword: 'access',
        path: '/standards'
    },
    {
        keyword: 'checklist',
        path: '/checklist'
    },
    {
        keyword: 'report',
        path: '/FAR'
    },
]

const staffPaths = ['/companies']

export const filteredRoutesByProduct = (contract: IContract | any, userProfile: IUser | undefined,  routesAttr: Array<RouteType>) => {
    let routes = routesAttr
    if (userProfile?.is_staff) {
        return routes
    }
    if (userProfile && !userProfile.is_staff) {
        routes = routes.filter((i) => !staffPaths.includes(i.path))
    }

    if (contract && contract.products.length > 0) {
        const { products } = contract
        const notIncludesUrl: string[] = []
        const nameOfProducts = products.flatMap((p: any) => p.name)

        trackedUrl.forEach(({ keyword, path }) => {
            const someIncludes = nameOfProducts
              .some((i: string) => (
                i.toLowerCase().includes(keyword.toLowerCase())
              ))
            if (!someIncludes) {
                notIncludesUrl.push(path)
            }
        })
        let filteredRoutes = routes.filter(({ path }) => (
          !notIncludesUrl.includes(path)
        ))

        filteredRoutes = filteredRoutes.map((route) => {
            if (route.children) {
                route.children = route.children
                  .filter(({ path }) => !notIncludesUrl.includes(path))
            }
            return route
        })

        filteredRoutes = filteredRoutes.filter(({ children }) => (
          children === null || children?.length > 0
        ))

        return filteredRoutes
    }
    return routes
}

export const checkDependsCondition = (a11yes: any, dependsOn: any): boolean => {
    const condition: boolean[] = []
    const foundA11y = (a11yes: any, depends: string) => {
        return a11yes.find((a11y: any) => (
          `${a11y.item}__${a11y.attribute}` === depends
        ))
    }

    if (!dependsOn) {
        condition.push(true)
    } else {
        if (dependsOn['AND']) {
            condition.push(dependsOn['AND'].every((depends: string) => {
                const a11y = foundA11y(a11yes, depends)
                return a11y && !a11y.is_absent
            }))
        }
        if (dependsOn['OR']) {
            condition.push(dependsOn['OR'].some((depends: string) => {
                const a11y = foundA11y(a11yes, depends)
                return a11y && !a11y.is_absent
            }))
        }
        if (dependsOn['NOT']) {
            condition.push(dependsOn['NOT'].every((depend: string) => {
                const parts: string[] = depend.split("__");
                if (parts.length === 3) {
                    const itemAttribute: string = parts.slice(0, 2).join("__");
                    for (const a11y of a11yes) {
                        const a11yItemAttribute: string = `${a11y.item}__${a11y.attribute}`.split("__").slice(0, 2).join("__");
                        if (itemAttribute === a11yItemAttribute) {
                            return depend !== `${a11y.item}__${a11y.attribute}`;
                        }
                    }
                } else {
                    const a11y = foundA11y(a11yes, depend)
                    return a11y && a11y.is_absent
                }
            }))
        }
        if (typeof dependsOn === 'string') {
            const a11y = foundA11y(a11yes, dependsOn)
            condition.push(!!(a11y && !a11y.is_absent))
        }
    }

    return condition.every((i) => i);
}

export const replaceText = (
  text: string,
  itemTitle: string = '',
  unitTitle: string = '',
  standardTitle: string = ''
) => {
    if (!text) {
        return text
    }

    return text
      .replace(/{item__title}/g, itemTitle.toLowerCase())
      .replace(/{unit__title}/g, unitTitle.toLowerCase())
      .replace(/{standard__title}/g, standardTitle.toLowerCase())
}

export const getReplacedTextForStandards = (standards: A11yStandardType[]): A11yStandardType[] => {
    return standards.map((standard) => ({
        ...standard,
        values: standard.values.map((a11yUnit) => ({
            ...a11yUnit,
            values: a11yUnit.values.map((a11yItem) => ({
                ...a11yItem,
                values: a11yItem.values.map((a11yAttribute) => ({
                    ...a11yAttribute,
                    q_title: replaceText(a11yAttribute.q_title, a11yItem.title, a11yUnit.title, standard.title),
                    title: replaceText(a11yAttribute.title, a11yItem.title, a11yUnit.title, standard.title),
                    values: a11yAttribute.values && a11yAttribute.values.map((a11yAttributeValue) => ({
                        ...a11yAttributeValue,
                        title: replaceText(a11yAttributeValue.title, a11yItem.title, a11yUnit.title, standard.title)
                    }))
                }))
            }))
        }))
    }))
}

export const range = (start: number, end: number) => {
    // @ts-ignore
    return Array(end - start + 1).fill().map((_, idx) => start + idx)
}

export const downloadImage = async (image: string) => {
    const imageName = image.substring(image.lastIndexOf("/") + 1);
    axios({
        url: `${image}?not-from-cache-please`,
        method: "GET",
        responseType: "blob",
        withCredentials: false,
    }).then((response: AxiosResponse<any>) => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(new Blob([response.data]))
        link.setAttribute("download", imageName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    });
};

export function arrayMove<T>(arr: T[], startIndex: number, endIndex: number): T[] {
    const [removed] = arr.splice(startIndex, 1);
    arr.splice(endIndex, 0, removed);
    return arr;
}


export const formDataAppendObject = (key: string, obj: any, fd: FormData) => {
    let i, k;
    for(i in obj) {
        k = key ? key + '.' + i : i;
        if(typeof obj[i] == 'object')
            formDataAppendObject(k, obj[i], fd);
        else
            fd.append(k, obj[i]);
    }
}

export const deepCompareObjects = (obj1: { [key: string]: any }, obj2: { [key: string]: any }): { [key: string]: any } | null => {
    const diff: { [key: string]: any } = {};

    const compareValues = (value1: any, value2: any, key: string) => {
        if (Array.isArray(value1) && Array.isArray(value2)) {
            if (JSON.stringify(value1) !== JSON.stringify(value2)) {
                diff[key] = value2;
            }
        } else if (
          (typeof value1 !== 'object' || value1 === null) &&
          (typeof value2 !== 'object' || value2 === null)
        ) {
            if (value1 !== value2) {
                diff[key] = value2;
            }
        } else if (value1 === null && value2 !== null) {
            diff[key] = value2;
        } else if (value1 !== null && value2 === null) {
            diff[key] = value2;
        } else if (typeof value1 === 'object' && typeof value2 === 'object') {
            const nestedDiff = deepCompareObjects(value1, value2);
            if (nestedDiff && Object.keys(nestedDiff).length > 0) {
                diff[key] = nestedDiff;
            }
        } else {
            diff[key] = value2;
        }
    };

    for (const key in obj1) {
        if (obj1.hasOwnProperty(key)) {
            if (obj2.hasOwnProperty(key)) {
                compareValues(obj1[key], obj2[key], key);
            } else {
                diff[key] = obj1[key];
            }
        }
    }

    for (const key in obj2) {
        if (obj2.hasOwnProperty(key) && !obj1.hasOwnProperty(key)) {
            diff[key] = obj2[key];
        }
    }

    return Object.keys(diff).length > 0 ? diff : null;
};

export const getTextByCurrentLanguage = (object: {[key: string]: string} | undefined) => {
    const { language } = i18n
    let text = object ? object['en'] : ''
    if (object && object[language]) {
        text = object[language]
    }
    return text
}

export const getDefaultLanguagesObject = (supportedLanguages: string[]) => {
    let object: {[key: string]: string} = { en: '' }
    if (supportedLanguages.length > 0) {
        supportedLanguages.forEach((lang) => {
            object[lang] = ''
        })
    }
    return object
}

export const correctionUnitName = (object: {[key: string]: string}) => {
    const { language } = i18n
    let copyObject = object
    if (copyObject && copyObject['en'] === '') {
        if (copyObject[language] && copyObject[language] !== '') {
            copyObject['en'] = copyObject[language]
        } else {
            const firstNotEmpty = Object.values(copyObject).find((i) => i !== '')
            if (firstNotEmpty) {
                copyObject['en'] = firstNotEmpty
            }
        }
    }
    return copyObject
}

export const removeSuffix = (language: string): string => {
    const index = language.indexOf('-');
    return index !== -1 ? language.substring(0, index) : language;
}

export const getCurrentIsoLanguage = (supportedLanguages: string[] | undefined) => {
    const { language } = i18n
    let currentIsoLanguage = 'en'
    if (supportedLanguages && supportedLanguages.length > 0 && supportedLanguages.includes(removeSuffix(language))) {
        currentIsoLanguage = language
    }
    return currentIsoLanguage
}

export const calculateDaysLeft = (targetDateString: string | null): number => {
    let daysLeft = 0
    if (targetDateString) {
        const targetDateObj = new Date(targetDateString);
        const currentDateObj = new Date();
        const difference = targetDateObj.getTime() - currentDateObj.getTime();
        daysLeft = Math.ceil(difference / (1000 * 60 * 60 * 24));
    }
    return daysLeft;
};

export const getCompanyNameById = (companies: any, companyId: string | undefined) => {
    if (companies?.length > 0 && companyId) {
        const findCompany = companies.find((i: any) => i.id === companyId)
        return findCompany ? findCompany.name : ''
    }
    return ''
}

export const processClpData = (clpData: Clp[]): Clp[] => {
    const attributeCounts: { [key: string]: number } = {};

    clpData.forEach((item) => {
        const attribute = item.attribute;
        if (attributeCounts[attribute]) {
            attributeCounts[attribute]++;
        } else {
            attributeCounts[attribute] = 1;
        }
    });

    clpData.forEach((item) => {
        item.count = String(attributeCounts[item.attribute] || 0);
    });

    return clpData.filter((item, index, self) =>
        index === self.findIndex((t) => (t.attribute === item.attribute))
    );
}